import { createContext, useContext, useState, useCallback, useRef, useMemo, useEffect } from 'react';
import type { ReactNode } from 'react';

export type ToastVariant = 'success' | 'error' | 'warning' | 'info' | 'loading';

export interface Toast {
  id: string;
  message: string;
  variant: ToastVariant;
  removing?: boolean;
}

export interface ConfirmOptions {
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
}

interface ToastContextValue {
  toasts: Toast[];
  toast: {
    success: (message: string) => string;
    error: (message: string) => string;
    warning: (message: string) => string;
    info: (message: string) => string;
    loading: (message: string) => string;
  };
  removeToast: (id: string) => void;
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  pendingConfirm: (ConfirmOptions & { resolve: (value: boolean) => void }) | null;
  resolveConfirm: (value: boolean) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

let toastCounter = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [pendingConfirm, setPendingConfirm] = useState<(ConfirmOptions & { resolve: (value: boolean) => void }) | null>(null);
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const animTimersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  // Cleanup all timers on unmount
  useEffect(() => {
    return () => {
      timersRef.current.forEach(t => clearTimeout(t));
      animTimersRef.current.forEach(t => clearTimeout(t));
    };
  }, []);

  const removeToast = useCallback((id: string) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setToasts(prev => prev.map(t => t.id === id ? { ...t, removing: true } : t));
    const animTimer = setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
      animTimersRef.current.delete(animTimer);
    }, 300);
    animTimersRef.current.add(animTimer);
  }, []);

  const addToast = useCallback((message: string, variant: ToastVariant): string => {
    const id = `toast-${++toastCounter}`;
    setToasts(prev => [...prev, { id, message, variant }]);

    if (variant === 'success' || variant === 'info' || variant === 'warning') {
      const timer = setTimeout(() => {
        removeToast(id);
        timersRef.current.delete(id);
      }, 4000);
      timersRef.current.set(id, timer);
    }

    return id;
  }, [removeToast]);

  const toast = useMemo(() => ({
    success: (message: string) => addToast(message, 'success'),
    error: (message: string) => addToast(message, 'error'),
    warning: (message: string) => addToast(message, 'warning'),
    info: (message: string) => addToast(message, 'info'),
    loading: (message: string) => addToast(message, 'loading'),
  }), [addToast]);

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise<boolean>(resolve => {
      setPendingConfirm({ ...options, resolve });
    });
  }, []);

  const resolveConfirm = useCallback((value: boolean) => {
    setPendingConfirm(prev => {
      if (prev) prev.resolve(value);
      return null;
    });
  }, []);

  return (
    <ToastContext.Provider value={useMemo(() => ({ toasts, toast, removeToast, confirm, pendingConfirm, resolveConfirm }), [toasts, toast, removeToast, confirm, pendingConfirm, resolveConfirm])}>
      {children}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
